/**
 * The Dupe Log: incidents from the anti-dupe system, with the item that was destroyed.
 *
 * <h2>The proof badge is the load-bearing part of this screen</h2>
 * `conclusive` means the same item was found in two slots of one inventory, which cannot happen
 * honestly — it is what triggers the automatic ban. `circumstantial` means the same item was seen on
 * two different players, which is *also* what a trade, a death-loot or a lagged sync looks like.
 *
 * So the two are styled to read as different kinds of thing, not as two shades of the same thing,
 * and the circumstantial one carries a sentence saying what else could explain it. A staff member
 * skim-reading this list at 2am should not be able to come away thinking a lagged trade was a
 * confession.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDupeIncidents } from "../api";
import { formatMoney, formatRelative, formatTimestamp } from "../format";
import { fetchDupeRefund, issueDupeRefund, NotAuthorizedError } from "../api";
import { prettyMaterial, stripLegacy } from "../minecraft";
import { ItemCard } from "./ItemCard";
import { IconClose } from "./Icons";
import type { DupeIncident, LogRow, PulledListing, RefundRecord } from "../types";

interface Props {
  onOpenPlayer: (player: string) => void;
  /** Needed to issue a refund, which is a state-changing POST. */
  csrfToken: string;
  /** DUPE rows arriving on the shared live stream, so a fresh incident appears without a refresh. */
  liveRows: LogRow[];
}

export function DupeTab({ onOpenPlayer, liveRows, csrfToken }: Props): JSX.Element {
  const [player, setPlayer] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState<DupeIncident | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (player.trim()) params.set("player", player.trim());
    if (from) params.set("from", String(Date.parse(from)));
    if (to) params.set("to", String(Date.parse(to) + 86_399_999));
    return params.toString();
  }, [player, from, to]);

  const incidents = useQuery({
    queryKey: ["dupe", query],
    queryFn: () => fetchDupeIncidents(query, null),
  });

  // A live DUPE row means something just happened. Rather than splice a half-formed incident into
  // the list — the ban row usually arrives a moment after the void — refetch, which also picks up
  // the grouping the server does.
  const liveDupeCount = liveRows.length;
  useEffect(() => {
    if (liveDupeCount === 0) return;
    const timer = setTimeout(() => void incidents.refetch(), 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveDupeCount]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const rows = incidents.data?.incidents ?? [];

  return (
    <>
      <div className="filterbar">
        <input
          className="input"
          placeholder="Player"
          value={player}
          onChange={(event) => setPlayer(event.target.value)}
          aria-label="Filter by player"
          style={{ width: "18ch" }}
        />
        <input
          className="input"
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          aria-label="From date"
          title="From date"
        />
        <input
          className="input"
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          aria-label="To date"
          title="To date"
        />
        {(player || from || to) && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setPlayer("");
              setFrom("");
              setTo("");
            }}
          >
            Clear
          </button>
        )}
        <span className="grow" />
        <span className="conn" style={{ fontSize: 12 }}>
          {rows.length} incident{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Player</th>
              <th>Server</th>
              <th>Finding</th>
              <th>What happened</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  {incidents.isLoading
                    ? "Loading…"
                    : incidents.isError
                      ? "Could not load the dupe log."
                      : "No dupe incidents recorded."}
                </td>
              </tr>
            )}
            {rows.map((incident) => (
              <tr
                key={incident.id}
                onClick={() => setOpen(incident)}
                style={{ cursor: "pointer" }}
                title="Open incident"
              >
                <td className="mono">
                  {formatTimestamp(incident.logged_at)}
                  <div style={{ fontSize: 10.5, color: "var(--text-3)" }}>
                    {formatRelative(incident.logged_at)}
                  </div>
                </td>
                <td>
                  {incident.player ? (
                    <button
                      type="button"
                      className="who"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenPlayer(incident.player as string);
                      }}
                    >
                      {incident.player}
                    </button>
                  ) : (
                    <span className="who-none">&mdash;</span>
                  )}
                </td>
                <td className="mono">{incident.server}</td>
                <td>
                  <ProofBadge proof={incident.proof} />
                  {/* Never "banned": the anti-dupe system does not ban. It takes the item and
                      stops the sale, and a human decides the rest. */}
                  {incident.enforcement?.auction_banned && (
                    <span className="tag" data-k="mute" style={{ marginLeft: 6 }}>
                      review
                    </span>
                  )}
                  {incident.refund?.status === "delivered" && (
                    <span className="tag" data-k="active" style={{ marginLeft: 6 }}>
                      refunded
                    </span>
                  )}
                  {incident.listings.length > 0 && (
                    <span className="tag" data-k="mute" style={{ marginLeft: 6 }}>
                      {incident.listings.length} pulled
                    </span>
                  )}
                </td>
                <td>
                  {incident.item ? (
                    <ItemSummary item={incident.item} />
                  ) : (
                    <span style={{ color: "var(--text-2)" }}>{incident.message}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <IncidentModal
          incident={open}
          onClose={() => setOpen(null)}
          onOpenPlayer={onOpenPlayer}
          csrfToken={csrfToken}
        />
      )}
    </>
  );
}

function ProofBadge({ proof }: { proof: string | null }): JSX.Element {
  if (proof === "conclusive") {
    return (
      <span className="tag" data-k="ban" title="The same item was in two slots of one inventory.">
        conclusive
      </span>
    );
  }
  if (proof === "circumstantial") {
    return (
      <span
        className="tag"
        data-k="mute"
        title="The same item was seen on two players — a trade or a lagged sync looks like this too."
      >
        lead only
      </span>
    );
  }
  return (
    <span className="tag" data-k="kick">
      unknown
    </span>
  );
}

/** One-line item summary for the table: name plus the headline enchantments. */
function ItemSummary({ item }: { item: Record<string, unknown> }): JSX.Element {
  const data = item as {
    material?: string;
    display_name?: string;
    enchantments?: Array<{ name?: string; level?: number }>;
  };
  // prettyMaterial, not the raw enum: "ENCHANTED_BOOK" in a list of otherwise human text reads as a
  // leaked internal, and the detail panel already shows the raw name for anyone who needs it.
  const name = data.display_name
    ? stripLegacy(data.display_name)
    : data.material
      ? prettyMaterial(data.material)
      : "Unknown item";
  const count = Array.isArray(data.enchantments) ? data.enchantments.length : 0;

  return (
    <>
      <span style={{ color: "var(--text)" }}>{name}</span>
      {count > 0 && (
        <span style={{ color: "var(--text-3)", fontSize: 11.5 }}>
          {" "}
          &middot; {count} enchantment{count === 1 ? "" : "s"}
        </span>
      )}
    </>
  );
}

function IncidentModal({
  incident,
  onClose,
  onOpenPlayer,
  csrfToken,
}: {
  incident: DupeIncident;
  onClose: () => void;
  onOpenPlayer: (player: string) => void;
  csrfToken: string;
}): JSX.Element {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (label: string, value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      // Clipboard is refused on insecure origins; the text is selectable regardless.
    }
  };

  const serialized =
    incident.item && typeof (incident.item as { serialized?: unknown }).serialized === "string"
      ? ((incident.item as { serialized: string }).serialized)
      : null;

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal"
        style={{ width: "min(640px, 100%)" }}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Dupe incident"
      >
        <div className="modal-head">
          <h2>Dupe incident</h2>
          <ProofBadge proof={incident.proof} />
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        {incident.proof === "circumstantial" && (
          <div className="note" data-k="warn">
            The same item was seen on two different players. A trade, a death drop or a lagged
            inventory sync all look like this &mdash; treat it as a lead to investigate, not as proof.
          </div>
        )}

        {incident.detailsMissing && (
          <div className="note">
            This row predates structured item logging, so only the summary below is available.
          </div>
        )}

        {incident.raw && (
          <div className="note" data-k="warn">
            The stored payload could not be parsed. Raw value:
            <div className="quote-block">{incident.raw}</div>
          </div>
        )}

        <p className="sub" style={{ marginBottom: 14 }}>
          {incident.message}
        </p>

        <Timeline incident={incident} />

        {incident.item && <ItemCard item={incident.item} />}

        <div className="item-section" style={{ marginTop: 14 }}>
          <h4>Incident</h4>
          <dl className="facts">
            <dt>Player</dt>
            <dd>
              {incident.player ? (
                <button type="button" className="who" onClick={() => onOpenPlayer(incident.player as string)}>
                  {incident.player}
                </button>
              ) : (
                "unknown"
              )}
            </dd>

            <dt>Server</dt>
            <dd className="mono">{incident.server}</dd>

            <dt>When</dt>
            <dd className="mono">{formatTimestamp(incident.logged_at)}</dd>

            {incident.voided_count !== null && (
              <>
                <dt>Copies destroyed</dt>
                <dd>{incident.voided_count}</dd>
              </>
            )}

            {incident.kind && (
              <>
                <dt>Detection</dt>
                <dd className="mono">{incident.kind}</dd>
              </>
            )}

            {incident.stamp && (
              <>
                <dt>Item stamp</dt>
                <dd>
                  <button
                    type="button"
                    className="copyable"
                    onClick={() => copy("stamp", incident.stamp as string)}
                    title="Click to copy"
                  >
                    {incident.stamp}
                  </button>
                  {copied === "stamp" && <span className="copied"> copied</span>}
                </dd>
              </>
            )}
          </dl>
        </div>

        <div className="item-section">
          <h4>Enforcement</h4>
          {incident.enforcement ? (
            <>
              <div className="note" data-k="warn">
                <b>No automatic ban.</b> The system took the item and stopped the sale — both
                reversible — but it does not end accounts. Whether this warrants a ban is a staff
                decision, made in game.
              </div>
              <dl className="facts">
                <dt>Server ban</dt>
                <dd>Awaiting staff review</dd>
                <dt>Auction access</dt>
                <dd>
                  {incident.enforcement.auction_banned ? "barred" : "unaffected"}
                  {incident.enforcement.auction_ban_reason &&
                    ` · ${incident.enforcement.auction_ban_reason}`}
                </dd>
                <dt>Listings pulled</dt>
                <dd>{incident.enforcement.auction_listings_pulled ?? 0}</dd>
              </dl>
            </>
          ) : (
            <div className="item-none">
              Nothing beyond destroying the duplicate. The system never bans on its own.
            </div>
          )}
        </div>

        {incident.listings.length > 0 && (
          <div className="item-section">
            <h4>
              Listings pulled ({incident.listings.length})
            </h4>
            <div className="listings">
              {incident.listings.map((listing) => (
                <ListingRow key={listing.rowId} listing={listing} flaggedPlayer={incident.player} />
              ))}
            </div>
          </div>
        )}

        {incident.voidLogId !== null && serialized && (
          <RefundPanel incident={incident} csrfToken={csrfToken} />
        )}

        {serialized && (
          <div className="item-section">
            <h4>Evidence</h4>
            <div className="item-none" style={{ marginBottom: 7 }}>
              The exact item, encoded, so it can be reconstructed during an appeal.
            </div>
            <button type="button" className="btn" onClick={() => copy("item", serialized)}>
              {copied === "item" ? "Copied" : "Copy item data"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The incident as a sequence: item voided, listings killed, player banned.
 *
 * Three rows in a table would make staff reconstruct the order themselves. Enforcement is a chain —
 * the ban happens *because* of the void, and the listings are pulled in between — and showing it as
 * one is the difference between reading the incident and decoding it.
 */
function Timeline({ incident }: { incident: DupeIncident }): JSX.Element | null {
  const steps: Array<{ at: number; label: string; tone?: string }> = [];

  if (incident.item || incident.voided_count !== null) {
    steps.push({
      at: incident.logged_at,
      label:
        incident.voided_count !== null
          ? `${incident.voided_count} cop${incident.voided_count === 1 ? "y" : "ies"} destroyed`
          : "Duplicate destroyed",
    });
  }

  if (incident.listings.length > 0) {
    const total = incident.listings.reduce((sum, l) => sum + (l.price ?? 0), 0);
    steps.push({
      at: Math.min(...incident.listings.map((l) => l.logged_at)),
      label:
        `${incident.listings.length} auction listing${incident.listings.length === 1 ? "" : "s"} pulled` +
        (total > 0 ? ` · ${formatMoney(total)} total` : ""),
      tone: "warn",
    });
  }

  if (incident.enforcement) {
    // Deliberately never "banned". The system bars the account from the auction house and stops
    // there; ending an account is a human decision made in game afterwards, and a timeline saying
    // "banned" would have staff believe it was already done.
    steps.push({
      at: incident.enforcement.logged_at,
      label: incident.enforcement.auction_banned
        ? `Barred from the auction house${
            incident.enforcement.auction_ban_reason
              ? ` · ${incident.enforcement.auction_ban_reason}`
              : ""
          } — awaiting staff review`
        : "Enforcement recorded — awaiting staff review",
      tone: "warn",
    });
  }

  for (const refund of incident.refundEvents) {
    steps.push({
      at: refund.logged_at,
      label:
        `Item returned${refund.issued_by ? ` by ${refund.issued_by}` : ""}` +
        (refund.dropped_on_ground ? " · dropped on the ground, inventory was full" : ""),
      tone: "ok",
    });
  }

  if (steps.length < 2) return null;

  return (
    <ol className="timeline-steps">
      {steps
        .sort((a, b) => a.at - b.at)
        .map((step, index) => (
          <li key={index} data-tone={step.tone}>
            <span className="ts-when">{formatTimestamp(step.at)}</span>
            <span className="ts-label">{step.label}</span>
          </li>
        ))}
    </ol>
  );
}

/** One destroyed listing. Expands into the same item view as the voided item. */
function ListingRow({
  listing,
  flaggedPlayer,
}: {
  listing: PulledListing;
  flaggedPlayer: string | null;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  // The person who listed it is not always the person who duped it — the item can be passed on
  // first. That is a lead worth following, so it is called out rather than left to be noticed.
  const differentSeller =
    listing.seller_name !== null &&
    flaggedPlayer !== null &&
    listing.seller_name.toLowerCase() !== flaggedPlayer.toLowerCase();

  return (
    <div className="listing">
      <div className="listing-head">
        <span className="listing-price">{formatMoney(listing.price)}</span>
        <span className="listing-meta">
          #{listing.listing_id ?? "?"} · {listing.seller_name ?? "unknown seller"}
          {listing.listed_at !== null && ` · listed ${formatRelative(listing.listed_at)}`}
        </span>
        <span className="grow" />
        {listing.item && (
          <button type="button" className="btn" style={{ padding: "2px 8px", fontSize: 12 }}
            onClick={() => setOpen((current) => !current)}>
            {open ? "Hide item" : "View item"}
          </button>
        )}
      </div>

      {differentSeller && (
        <div className="listing-flag">
          Listed by <b>{listing.seller_name}</b>, not {flaggedPlayer} — the item changed hands before
          it was sold.
        </div>
      )}

      {open && listing.item && (
        <div style={{ marginTop: 9 }}>
          <ItemCard item={listing.item} />
        </div>
      )}
    </div>
  );
}

/**
 * Approve the item's return.
 *
 * The website never talks to the game servers, and nothing is pushed into anybody's inventory: this
 * queues a row that waits until the PLAYER runs `/claimrefund`. So the button approves a refund, it
 * does not deliver one, and the wording throughout says so — a staff member who reads "refunded" and
 * closes the ticket has been misled if the player has not collected it yet.
 *
 * That also means `pending` is not a stuck job. It can sit for days while the player is away or
 * simply has not run the command, which is why the pending state says as much rather than looking
 * like something failed.
 *
 * The returned copy gets a NEW identity. The copy that was not voided still carries the old one, so
 * handing the original back would put two items with one stamp into circulation and the next scan
 * would void one of them again. Where a stamp is shown here it is the new one, labelled as such.
 */
function RefundPanel({
  incident,
  csrfToken,
}: {
  incident: DupeIncident;
  csrfToken: string;
}): JSX.Element {
  const [refund, setRefund] = useState<RefundRecord | null>(incident.refund);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const issue = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await issueDupeRefund(incident.voidLogId as number, csrfToken);
      setRefund(result.refund);
      setConfirming(false);
    } catch (caught) {
      setError(
        caught instanceof NotAuthorizedError
          ? "Your role does not include issuing refunds."
          : caught instanceof Error
            ? caught.message
            : "Could not queue the refund.",
      );
    } finally {
      setBusy(false);
    }
  };

  // Poll while the refund has not landed, so a panel left open updates when the player finally
  // claims. Twenty seconds rather than five: `pending` waits on a human running a command and can
  // last days, so there is nothing to be gained by asking constantly, and `claimed` is sub-second —
  // it will usually be skipped straight past to `delivered`, which is fine.
  const inFlight = refund !== null && (refund.status === "pending" || refund.status === "claimed");
  useEffect(() => {
    if (!inFlight || refund === null) return;
    const timer = setInterval(() => {
      void fetchDupeRefund(refund.id)
        .then((result) => setRefund(result.refund))
        .catch(() => undefined);
    }, 20_000);
    return () => clearInterval(timer);
  }, [inFlight, refund?.id]);

  const delivered = incident.refundEvents[0] ?? null;

  return (
    <div className="item-section">
      <h4>Refund</h4>

      {refund === null ? (
        <>
          <div className="item-none" style={{ marginBottom: 9 }}>
            Approves the return of this exact item to {incident.player ?? "the player"}. They collect
            it themselves in game with <code>/claimrefund</code> — nothing is put into their
            inventory, and they are told once per session that something is waiting.
          </div>
          {error && (
            <div className="note" data-k="danger">
              {error}
            </div>
          )}
          {confirming ? (
            <div className="row">
              <span style={{ fontSize: 12.5, color: "var(--warn)" }}>
                Approve this refund? It cannot be withdrawn once approved.
              </span>
              <span className="grow" />
              <button type="button" className="btn" onClick={() => setConfirming(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn" data-variant="primary" onClick={issue} disabled={busy}>
                {busy ? "Approving…" : "Yes, approve"}
              </button>
            </div>
          ) : (
            <button type="button" className="btn" onClick={() => setConfirming(true)}>
              Approve refund
            </button>
          )}
        </>
      ) : (
        <RefundState refund={refund} delivered={delivered} />
      )}
    </div>
  );
}

function RefundState({
  refund,
  delivered,
}: {
  refund: RefundRecord;
  delivered: DupeIncident["refundEvents"][number] | null;
}): JSX.Element {
  if (refund.status === "delivered") {
    const stamp = refund.new_stamp ?? delivered?.new_stamp ?? null;
    return (
      <>
        <div className="note">
          <b>Returned.</b> Delivered{" "}
          {refund.delivered_at ? formatRelative(refund.delivered_at) : "recently"}
          {refund.issued_by && `, issued by ${refund.issued_by}`}
          {delivered?.dropped_on_ground && " — their inventory was full, so it was dropped at their feet"}
          .
        </div>
        {stamp && (
          <dl className="facts">
            <dt>New item stamp</dt>
            <dd className="mono" style={{ fontSize: 11.5 }}>
              {stamp}
            </dd>
          </dl>
        )}
        <div className="item-none">
          The returned copy carries a new identity, not the original — the un-voided copy still has
          that one, and reusing it would get the refund voided again on the next scan.
        </div>
      </>
    );
  }

  if (refund.status === "failed") {
    return (
      <div className="note" data-k="danger">
        <b>Refund failed.</b> The stored item could not be decoded, so nothing was given back. This
        one needs a human — issued by {refund.issued_by ?? "unknown"}{" "}
        {formatRelative(refund.created_at)}.
      </div>
    );
  }

  if (refund.status === "claimed") {
    return (
      <div className="note" data-k="warn">
        <b>Being handed over.</b> The player has run <code>/claimrefund</code> and{" "}
        {refund.claimed_by ?? "a server"} is giving the item back now. Approved by{" "}
        {refund.issued_by ?? "unknown"} {formatRelative(refund.created_at)}.
      </div>
    );
  }

  // Deliberately not framed as pending-and-therefore-late. This state is the normal one and it can
  // last days: the item only moves when the player asks for it.
  return (
    <div className="note">
      <b>Approved — not yet collected.</b> Waiting for the player to run <code>/claimrefund</code>.
      They are reminded once each time they log in, so this can sit for days without anything being
      wrong. Approved by {refund.issued_by ?? "unknown"} {formatRelative(refund.created_at)}.
    </div>
  );
}
